import { supabase } from "@/lib/supabase";
import type { BadgeEntityType, BadgeRuleConfig } from "@/lib/badgeRules";

/** Sum of (metrics[key] ?? 0) * weights[key] for each key in weights. */
export function computeWeightedScore(
  metrics: Record<string, number>,
  weights: Record<string, number>,
): number {
  let score = 0;
  for (const [key, weight] of Object.entries(weights)) {
    score += (metrics[key] ?? 0) * weight;
  }
  return score;
}

function sinceIsoFromHours(sinceHours: number): string {
  return new Date(Date.now() - sinceHours * 60 * 60 * 1000).toISOString();
}

export async function fetchInterestCounts(
  entityType: BadgeEntityType,
  entityIds: string[],
  sinceHours: number,
): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (entityIds.length === 0) return counts;

  const { data, error } = await supabase.rpc("get_interest_counts", {
    p_entity_type: entityType,
    p_entity_ids: entityIds,
    p_since: sinceIsoFromHours(sinceHours),
  });

  // Anon/guest has no EXECUTE on this SECURITY DEFINER RPC — empty counts, keep page load.
  if (error) {
    console.warn("[fetchInterestCounts] unavailable, using empty counts", error.message);
    return counts;
  }

  for (const row of data ?? []) {
    if (!row.entity_id) continue;
    counts.set(row.entity_id, Number(row.interest_count));
  }
  return counts;
}

export async function fetchProposalCounts(
  entityType: BadgeEntityType,
  entityIds: string[],
  sinceHours: number,
): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (entityIds.length === 0) return counts;

  const { data, error } = await supabase.rpc("get_proposal_counts", {
    p_entity_type: entityType,
    p_entity_ids: entityIds,
    p_since: sinceIsoFromHours(sinceHours),
  });

  // Anon/guest has no EXECUTE on this SECURITY DEFINER RPC — empty counts, keep page load.
  if (error) {
    console.warn("[fetchProposalCounts] unavailable, using empty counts", error.message);
    return counts;
  }

  for (const row of data ?? []) {
    if (!row.entity_id) continue;
    counts.set(row.entity_id, Number(row.proposal_count));
  }
  return counts;
}

export async function fetchWindowedBadgeCounts(
  entityType: BadgeEntityType,
  entityIds: string[],
  windowHours: number,
): Promise<{ interestCounts: Map<string, number>; proposalCounts: Map<string, number> }> {
  const [interestCounts, proposalCounts] = await Promise.all([
    fetchInterestCounts(entityType, entityIds, windowHours),
    fetchProposalCounts(entityType, entityIds, windowHours),
  ]);
  return { interestCounts, proposalCounts };
}

/** @deprecated Use fetchWindowedBadgeCounts */
export const fetchWindowedPopularCounts = fetchWindowedBadgeCounts;

/**
 * Shared interest + proposal metrics for weighted badge scoring.
 * ticket_velocity is not yet available — requires a future windowed ticket RPC.
 */
export function buildBadgeScoreMetrics(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  pulseRating?: number | null,
  pulseRatingCount?: number | null,
): Record<string, number> {
  const metrics: Record<string, number> = {
    interest_count: interestCounts.get(entityId) ?? 0,
    proposal_count: proposalCounts.get(entityId) ?? 0,
  };
  if ((pulseRatingCount ?? 0) >= 5 && pulseRating != null) {
    metrics.pulse_rating = Number(pulseRating);
  }
  return metrics;
}

/** @deprecated Use buildBadgeScoreMetrics */
export const buildPopularMetrics = buildBadgeScoreMetrics;

function computeEntityBadgeScore(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
  pulseRating?: number | null,
  pulseRatingCount?: number | null,
): boolean {
  if (!rule?.enabled) return false;
  const metrics = buildBadgeScoreMetrics(
    entityId,
    interestCounts,
    proposalCounts,
    pulseRating,
    pulseRatingCount,
  );
  const score = computeWeightedScore(metrics, rule.metricWeights);
  return score >= rule.threshold;
}

export function computeEntityPopular(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
): boolean {
  return computeEntityBadgeScore(entityId, interestCounts, proposalCounts, rule);
}

export function computeEntityTopPick(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
  pulseRating?: number | null,
  pulseRatingCount?: number | null,
): boolean {
  return computeEntityBadgeScore(
    entityId,
    interestCounts,
    proposalCounts,
    rule,
    pulseRating,
    pulseRatingCount,
  );
}

export function computeEntityBadgeScoreDetails(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
  resultKey: "isPopular" | "isTopPick",
  pulseRating?: number | null,
  pulseRatingCount?: number | null,
): {
  metrics: Record<string, number>;
  score: number;
  threshold: number;
  isPopular?: boolean;
  isTopPick?: boolean;
} {
  const metrics = buildBadgeScoreMetrics(
    entityId,
    interestCounts,
    proposalCounts,
    resultKey === "isTopPick" ? pulseRating : undefined,
    resultKey === "isTopPick" ? pulseRatingCount : undefined,
  );
  const threshold = rule?.threshold ?? 0;
  const score = rule?.enabled ? computeWeightedScore(metrics, rule.metricWeights) : 0;
  const passes = rule?.enabled ? score >= threshold : false;
  return {
    metrics,
    score,
    threshold,
    [resultKey]: passes,
  };
}

export function computeEntityPopularScore(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
): { metrics: Record<string, number>; score: number; threshold: number; isPopular: boolean } {
  const result = computeEntityBadgeScoreDetails(
    entityId,
    interestCounts,
    proposalCounts,
    rule,
    "isPopular",
  );
  return {
    metrics: result.metrics,
    score: result.score,
    threshold: result.threshold,
    isPopular: result.isPopular ?? false,
  };
}

export function computeEntityTopPickScore(
  entityId: string,
  interestCounts: Map<string, number>,
  proposalCounts: Map<string, number>,
  rule: BadgeRuleConfig | undefined,
  pulseRating?: number | null,
  pulseRatingCount?: number | null,
): { metrics: Record<string, number>; score: number; threshold: number; isTopPick: boolean } {
  const result = computeEntityBadgeScoreDetails(
    entityId,
    interestCounts,
    proposalCounts,
    rule,
    "isTopPick",
    pulseRating,
    pulseRatingCount,
  );
  return {
    metrics: result.metrics,
    score: result.score,
    threshold: result.threshold,
    isTopPick: result.isTopPick ?? false,
  };
}
